import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  EMPTY,
  Subject,
  catchError,
  debounceTime,
  groupBy,
  mergeMap,
  switchMap,
} from 'rxjs';
import { AuthService } from './auth.service';

/**
 * ECDEV-365: the stage a preview was opened from. The two stages print for different reasons, so
 * they start the print-layout switch in different positions.
 */
export enum PrintLayoutStage {
  /** Working copies under review. Reviewers read them on plain paper, so nothing is stripped. */
  Stage3 = 'stage3',
  /** The official documents. Cold Storage prints these onto pre-printed stock, so the form is stripped. */
  Stage4 = 'stage4',
}

/**
 * ECDEV-365: the switch position a user meets a document with, per stage, before they have ever set
 * it themselves. True renders the pre-printed form background ("the boxes"), false renders the typed
 * values on blank pages.
 *
 * NOTE ON THE TICKET'S WORDING: ECDEV-365 asks for Stage 4 to "start On", meaning the boxes stripped
 * — its "on" is the stripping, not this switch. In the switch's own vocabulary, ON draws the boxes,
 * so Stage 4's default is FALSE. The behaviour is the one the ticket describes: Stage 3 prints the
 * full document, Stage 4 prints values only.
 */
export const PRINT_LAYOUT_STAGE_DEFAULTS: Readonly<Record<PrintLayoutStage, boolean>> = {
  [PrintLayoutStage.Stage3]: true,
  [PrintLayoutStage.Stage4]: false,
};

/**
 * ECDEV-174: remembers, per signed-in user and per document TEMPLATE, whether the rendered PDF
 * should include the pre-printed form background ("print layout").
 *
 * There is no preferences screen. The value is written the moment the user flips the switch in a
 * preview modal, and read back the next time that user opens that document — in any stage, in any
 * order. Whether you want the background really answers "does my printer have the pre-printed form
 * loaded for this document type", which is why the key is the template and not the order.
 *
 * ECDEV-365: what changed is only the FALLBACK. A stored choice still wins everywhere, and is still
 * one value per user per template rather than one per stage — so a user who sets the switch in
 * Stage 3 keeps that setting when they meet the same document in Stage 4, exactly as before. The
 * per-stage defaults apply where there is no stored choice, which is why "no stored choice" and
 * "stored false" must stay distinguishable all the way from the server: see the nullable
 * printWithBackground on OrderDocumentPreviewDto.
 *
 * READS COST NOTHING. The value rides along on the document-preview response that both Stage 3 and
 * Stage 4 already have to call, so paging through the Stage-3 gallery issues no extra requests at
 * all. This cache only exists so a document already seen this session shows the right switch
 * position instantly, before its (slow) preview render comes back.
 *
 * WRITES ARE BEST-EFFORT. They are applied to the cache immediately, coalesced per template so
 * flicking the switch back and forth sends one request, and swallowed on failure: a preference that
 * fails to save must never block the viewer or raise a toast.
 */
@Injectable({ providedIn: 'root' })
export class DocumentPrintPreferenceService {
  private readonly saveDebounceMs = 400;

  /**
   * documentId (template) -> the user's OWN choice. Absent means "this user has never set one",
   * which is not the same as a stored false: the stage default answers for the first, the stored
   * value for the second. Never write a default in here — the same cache is read from both stages.
   */
  private cache = new Map<string, boolean>();

  /**
   * Templates the user changed in this session. A server echo must not clobber these: a preview
   * response can arrive while a debounced write is still queued, and would otherwise hand back the
   * pre-flip value.
   */
  private locallySet = new Set<string>();

  private saves = new Subject<{ documentId: string; printWithBackground: boolean }>();

  constructor(
    private http: HttpClient,
    private auth: AuthService,
  ) {
    this.saves
      .pipe(
        // Debounce PER TEMPLATE. A single shared debounce would drop the write for document A when
        // the user flips document B inside the window.
        groupBy((save) => save.documentId),
        mergeMap((byTemplate) =>
          byTemplate.pipe(
            debounceTime(this.saveDebounceMs),
            switchMap((save) =>
              this.http
                .put<void>(
                  `/personal/document-print-preferences/${save.documentId}`,
                  { printWithBackground: save.printWithBackground },
                )
                // catchError on the INNER observable only, so one failed write cannot kill the
                // outer subscription and silently stop every later save.
                .pipe(catchError(() => EMPTY)),
            ),
          ),
        ),
      )
      .subscribe();

    // Signing out does not reload the SPA, so without this the next user in the same tab would
    // inherit the previous user's choices.
    this.auth.user.subscribe((user) => {
      if (!user) {
        this.cache.clear();
        this.locallySet.clear();
      }
    });
  }

  /** ECDEV-365: the first-visit position for a stage, with no user choice in play. */
  defaultFor(stage: PrintLayoutStage): boolean {
    return PRINT_LAYOUT_STAGE_DEFAULTS[stage];
  }

  /**
   * The switch position to open with: the user's own choice for this template if they have one,
   * otherwise the default for the stage asking (ECDEV-365).
   */
  get(documentId: string | null | undefined, stage: PrintLayoutStage): boolean {
    if (!documentId) {
      return this.defaultFor(stage);
    }
    return this.cache.get(documentId) ?? this.defaultFor(stage);
  }

  /**
   * Seeds the cache from a preview response. A choice made in this session always wins.
   *
   * ECDEV-365: a null from the server means "never set" and must leave the cache empty so the
   * caller falls through to its stage default. Storing it as false would have pinned the document
   * to Stage 4's answer in Stage 3 too, for a user who had never touched the switch.
   */
  prime(
    documentId: string | null | undefined,
    printWithBackground: boolean | null | undefined,
  ): void {
    if (!documentId || this.locallySet.has(documentId)) {
      return;
    }
    if (printWithBackground == null) {
      this.cache.delete(documentId);
      return;
    }
    this.cache.set(documentId, printWithBackground);
  }

  /** Records a user flip: applied locally at once, persisted in the background. */
  set(documentId: string | null | undefined, printWithBackground: boolean): void {
    if (!documentId) {
      return;
    }
    this.cache.set(documentId, printWithBackground);
    this.locallySet.add(documentId);
    this.saves.next({ documentId, printWithBackground });
  }
}
